import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, CircularProgress, Tooltip } from '@material-ui/core';
import { ImageLoader } from '@sharedComponents/ImageLoader/ImageLoader';
import { DeleteModal } from '@components/Modals';
import {
  makeCurrentAnswer,
  makeDeleteAnswers,
  setCurrentAnswer,
  setViewMode,
} from '@actions/answerActions';
import { MgState, UserEntityInterface } from '@types';
import {
  createPersonImageAttach,
  convertImageToBase64,
  generateAnswerEml,
  generateInitialsAvatar,
  HtmlGenerator,
  translate,
} from '@utils';
import { ArrowLeftBoldIcon, DownloadIcon, EditIcon, TrashIcon } from '@images/icons';
import { AnswerPaths, Paths, QuestionnairePaths } from '@constants/paths';
import { answerEnvironmentSelector } from '@selectors/answerEnvironmentSelector';
import {
  currentAnswerLanguageSelector,
  currentAnswerSelector,
  isOwnAnswerSelector,
} from '@selectors/answer';
import { setAnswerAttachments, setAnswerEnvLanguage } from '@actions/answerEnvironmentActions';
import { failure, loading } from '@actions/commonActions';
import { hasPermissionFeature } from '@hooks/hasPermissionFeature';
import { FeaturePermission, LangEnum, ImageShape, PagePermission, PermissionActions } from '@enums';

import './index.scss';

const AnswerContainer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  const currentAnswer = useSelector(currentAnswerSelector);
  const isOwnAnswer = useSelector(isOwnAnswerSelector);
  const env = useSelector(answerEnvironmentSelector);
  const lang = useSelector(currentAnswerLanguageSelector);
  const isCurrentAnswerFailure = useSelector<MgState, boolean>(
    ({ answer }) => answer.isCurrentAnswerFailure
  );
  const isCurrentAnswerLoading = useSelector<MgState, boolean>(
    ({ answer }) => answer.isCurrentAnswerLoading && !answer.currentAnswer
  );

  const barLoading = useSelector<MgState, boolean>(({ app }) => app.barLoading);

  /*
  The useEffect for moderator roles and higher
   */
  useEffect(() => {
    if (id) {
      dispatch(makeCurrentAnswer(id));
    }
  }, [id]);

  useEffect(() => {
    dispatch(setViewMode(false));
  }, []);

  const onChangeClick = () => {
    dispatch(setViewMode(true));
    dispatch(setCurrentAnswer(currentAnswer));
    navigate(`/${QuestionnairePaths.QUESTIONNAIRE}/${currentAnswer.questionnaire.id}`);
  };

  useEffect(() => {
    if (isCurrentAnswerFailure) {
      navigate(`/${AnswerPaths.NOT_FOUND}`);
    }
    return () => {
      dispatch(failure(false, 'current_answer'));
    };
  }, [isCurrentAnswerFailure]);

  useEffect(() => {
    if (lang && env.lang !== lang) {
      dispatch(setAnswerEnvLanguage(currentAnswer?.questionnaire?.lang));
    }
    if (currentAnswer) {
      const answerHtml = new HtmlGenerator(currentAnswer.questionnaire.renderer.data, env);
      answerHtml.renderHtml();
    }
  }, [currentAnswer, env]);

  const handleOpenDelete = () => {
    setIsOpenDelete(true);
  };

  const handleCancelDelete = () => {
    setIsOpenDelete(false);
  };

  const handleConfirmDelete = useCallback(() => {
    try {
      dispatch(makeDeleteAnswers({ answersIds: [currentAnswer.id] }));
      if (!barLoading) {
        navigate(`/${AnswerPaths.ANSWERS}`);
      }
    } finally {
      setIsOpenDelete(false);
    }
  }, [dispatch, currentAnswer, barLoading]);

  const handleDownload = useCallback(() => {
    try {
      dispatch(loading(true, 'bar'));
      generateAnswerEml(`Questionnaire ${currentAnswer.owner.fullNameEng}`, currentAnswer);
    } finally {
      dispatch(loading(false, 'bar'));
    }
  }, [currentAnswer]);

  const handleOnImageLoad = (event, id) => {
    dispatch(
      setAnswerAttachments([
        ...env.attachments,
        createPersonImageAttach(id, {
          [ImageShape.SQUARE]: convertImageToBase64(event.target, ImageShape.SQUARE),
          [ImageShape.CIRCLE]: convertImageToBase64(event.target, ImageShape.CIRCLE),
        }),
      ])
    );
  };

  const handleOnImageError = (
    event: React.SyntheticEvent<HTMLImageElement, Event>,
    user: UserEntityInterface
  ) => {
    const initials = lang === LangEnum.RU ? user?.fullNameRu : user?.fullNameEng;
    const squareAvatar = generateInitialsAvatar(initials, ImageShape.SQUARE);
    const circleAvatar = generateInitialsAvatar(initials, ImageShape.CIRCLE);
    dispatch(
      setAnswerAttachments([
        ...env.attachments,
        createPersonImageAttach(user.id, {
          [ImageShape.SQUARE]: squareAvatar,
          [ImageShape.CIRCLE]: circleAvatar,
        }),
      ])
    );
  };

  const handleLinkClick = (event: React.MouseEvent) => {
    if (isCurrentAnswerLoading) {
      event.preventDefault();
    }
  };

  const canRemoveAnswer = hasPermissionFeature(
    PagePermission.Answers,
    [FeaturePermission.AnswerItem],
    [PermissionActions.Remove]
  );
  const canUpdateAnswer = hasPermissionFeature(
    PagePermission.Answers,
    [FeaturePermission.AnswerItem],
    [PermissionActions.Update]
  );
  const canDownloadAnswer = hasPermissionFeature(
    PagePermission.Answers,
    [FeaturePermission.AnswerItem],
    [PermissionActions.Download]
  );
  const hasControllers = canRemoveAnswer || (canUpdateAnswer && isOwnAnswer) || canDownloadAnswer;

  return (
    <div className="answer-page">
      {hasControllers && (
        <div className="answer-controllers">
          <NavLink onClick={handleLinkClick} to={Paths.ANSWERS} className="button-back">
            <Button
              disableRipple
              disabled={isCurrentAnswerLoading}
              className="answer-button"
              size="large"
              data-testid="button-back"
              startIcon={<ArrowLeftBoldIcon />}
            >
              {translate('back')}
            </Button>
          </NavLink>
          <div className="button-container">
            {(canUpdateAnswer || isOwnAnswer) && (
              <span className="answer-button" data-testid="button-edit">
                <Tooltip title={translate('edit')} arrow>
                  <span>
                    <Button
                      disableRipple
                      disabled={isCurrentAnswerLoading}
                      onClick={onChangeClick}
                      size="large"
                      color="primary"
                    >
                      <EditIcon />
                    </Button>
                  </span>
                </Tooltip>
              </span>
            )}
            {canRemoveAnswer && (
              <>
                <span className="answer-button" data-testid="button-delete">
                  <Tooltip title={translate('delete')} arrow>
                    <span>
                      <Button
                        disableRipple
                        disabled={isCurrentAnswerLoading}
                        size="large"
                        color="primary"
                        onClick={handleOpenDelete}
                      >
                        <TrashIcon />
                      </Button>
                    </span>
                  </Tooltip>
                </span>
                <DeleteModal
                  open={isOpenDelete}
                  handleCancel={handleCancelDelete}
                  handleConfirmation={handleConfirmDelete}
                  contentName="caution_message"
                  title={translate('answer-deleted-modal')}
                  isDisabledConfirmationButton={barLoading}
                />
              </>
            )}
            {canDownloadAnswer && (
              <span className="answer-button" data-testid="button-download">
                <Tooltip title={translate('download')} arrow>
                  <span>
                    <Button
                      onClick={handleDownload}
                      disableRipple
                      disabled={isCurrentAnswerLoading}
                      size="large"
                    >
                      <DownloadIcon />
                    </Button>
                  </span>
                </Tooltip>
              </span>
            )}
          </div>
        </div>
      )}
      <div className="message-user-container">
        {!hasControllers && isOwnAnswer && (
          <div className="button-user-container">
            <Tooltip title={translate('edit')} arrow>
              <span>
                <Button
                  disableRipple
                  disabled={isCurrentAnswerLoading}
                  onClick={onChangeClick}
                  className="answer-button-user"
                  size="large"
                  color="primary"
                >
                  <EditIcon />
                </Button>
              </span>
            </Tooltip>
          </div>
        )}

        {isCurrentAnswerLoading && (
          <div className="message-container__loader">
            <CircularProgress />
          </div>
        )}
        {!isCurrentAnswerLoading && (
          <div id="message" data-testid="message" className="message-container"></div>
        )}
      </div>
      <ImageLoader
        userId={currentAnswer?.owner?.id}
        onLoad={(event) => handleOnImageLoad(event, currentAnswer?.owner?.id)}
        onError={(event) => handleOnImageError(event, currentAnswer?.owner)}
      />
    </div>
  );
};

export { AnswerContainer };
